import { useMemo, type ReactNode } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import type { GridFilterModel } from '@mui/x-data-grid';

export interface FilterChoice {
  /** Value written into the Data Grid filter model. */
  readonly value: string;
  readonly label: string;
  readonly count?: number;
}

export interface FilterDefinition {
  /** Grid column this chip group filters. */
  readonly field: string;
  readonly label: string;
  readonly choices: readonly FilterChoice[];
  /** Allow several chips at once (`isAnyOf`) instead of one (`equals`). */
  readonly multiple?: boolean;
}

export interface SearchAndFilterValue {
  readonly search: string;
  /** field → selected values. Empty array means "no filter on this field". */
  readonly filters: Readonly<Record<string, readonly string[]>>;
}

export const emptySearchAndFilter: SearchAndFilterValue = { search: '', filters: {} };

export interface SearchAndFilterProps {
  readonly value: SearchAndFilterValue;
  readonly onChange: (value: SearchAndFilterValue) => void;
  readonly definitions: readonly FilterDefinition[];
  readonly searchPlaceholder?: string;
  /** Result count shown next to the filters; omit while loading. */
  readonly resultCount?: number;
  /** Slot for a saved-views control — deliberately injected, not owned by this pattern. */
  readonly savedViews?: ReactNode;
}

/**
 * Turns chip selections into a Data Grid `filterModel` (backbone §6 pattern rules).
 *
 * Chips beat a filter drawer here because the active filter set stays *visible*: a support call
 * that starts with "I don't see the policy" is usually a filter nobody remembered was on.
 */
export function toFilterModel(
  value: SearchAndFilterValue,
  definitions: readonly FilterDefinition[],
): GridFilterModel {
  const items = definitions.flatMap((definition) => {
    const selected = value.filters[definition.field] ?? [];
    if (selected.length === 0) return [];
    return [
      definition.multiple === true || selected.length > 1
        ? {
            id: definition.field,
            field: definition.field,
            operator: 'isAnyOf',
            value: [...selected],
          }
        : { id: definition.field, field: definition.field, operator: 'equals', value: selected[0] },
    ];
  });
  return {
    items,
    quickFilterValues: value.search.trim() === '' ? [] : value.search.trim().split(/\s+/),
  };
}

export function SearchAndFilter({
  value,
  onChange,
  definitions,
  searchPlaceholder,
  resultCount,
  savedViews,
}: SearchAndFilterProps): ReactNode {
  const { t } = useTranslation('patterns');
  const selects = useTheme().eis.surface.filterStyle === 'selects';

  const activeCount = useMemo(
    () => Object.values(value.filters).reduce((total, selected) => total + selected.length, 0),
    [value.filters],
  );

  const toggle = (definition: FilterDefinition, choice: FilterChoice): void => {
    const selected = value.filters[definition.field] ?? [];
    const isSelected = selected.includes(choice.value);
    const next =
      definition.multiple === true
        ? isSelected
          ? selected.filter((entry) => entry !== choice.value)
          : [...selected, choice.value]
        : isSelected
          ? []
          : [choice.value];
    onChange({ ...value, filters: { ...value.filters, [definition.field]: next } });
  };

  const setField = (definition: FilterDefinition, next: readonly string[]): void => {
    onChange({ ...value, filters: { ...value.filters, [definition.field]: next } });
  };

  const choiceLabel = (choice: FilterChoice): string =>
    choice.count === undefined ? choice.label : `${choice.label} (${String(choice.count)})`;

  /**
   * Berry (design v2, `theme.eis.surface.filterStyle`): the search field, one `Select` per filter
   * showing the filter's name, the result count on the right, and the active choices as chips
   * that remove themselves — the active set stays visible, as it does with suite's chip groups.
   */
  if (selects) {
    const active = definitions.flatMap((definition) =>
      (value.filters[definition.field] ?? []).map((selected) => ({
        definition,
        value: selected,
        label: definition.choices.find((choice) => choice.value === selected)?.label ?? selected,
      })),
    );
    return (
      <Stack spacing={1.5}>
        <Stack
          direction="row"
          spacing={1.5}
          useFlexGap
          sx={{ alignItems: 'center', flexWrap: 'wrap' }}
        >
          <TextField
            value={value.search}
            onChange={(event) => {
              onChange({ ...value, search: event.target.value });
            }}
            placeholder={searchPlaceholder ?? t('filter.searchPlaceholder')}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
              htmlInput: { 'aria-label': t('actions.search') },
            }}
            sx={{ width: 320, maxWidth: '100%' }}
          />
          {definitions.map((definition) => {
            const selected = value.filters[definition.field] ?? [];
            return (
              <Select<string[]>
                key={definition.field}
                multiple
                displayEmpty
                value={[...selected]}
                onChange={(event) => {
                  const next =
                    typeof event.target.value === 'string'
                      ? event.target.value.split(',')
                      : event.target.value;
                  // A single-choice filter keeps the latest pick only.
                  setField(
                    definition,
                    definition.multiple === true ? next : next.filter((v) => !selected.includes(v)),
                  );
                }}
                renderValue={() =>
                  selected.length === 0
                    ? definition.label
                    : `${definition.label} (${String(selected.length)})`
                }
                // The combobox is named by its filter; its shown value says what is picked.
                SelectDisplayProps={{ 'aria-label': definition.label }}
                sx={{ minWidth: 120 }}
              >
                {definition.choices.map((choice) => (
                  <MenuItem key={choice.value} value={choice.value}>
                    {choiceLabel(choice)}
                  </MenuItem>
                ))}
              </Select>
            );
          })}
          {savedViews !== undefined && <Box>{savedViews}</Box>}
          <Box sx={{ flex: 1 }} />
          {resultCount !== undefined && (
            <Typography variant="body2" color="text.secondary" aria-live="polite">
              {t('filter.resultCount', { count: resultCount })}
            </Typography>
          )}
        </Stack>
        {active.length > 0 && (
          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            role="group"
            aria-label={t('filter.activeFilters')}
            sx={{ alignItems: 'center', flexWrap: 'wrap' }}
          >
            {active.map((entry) => (
              <Chip
                key={`${entry.definition.field}:${entry.value}`}
                label={`${entry.definition.label}: ${entry.label}`}
                variant="outlined"
                onDelete={() => {
                  setField(
                    entry.definition,
                    (value.filters[entry.definition.field] ?? []).filter(
                      (selected) => selected !== entry.value,
                    ),
                  );
                }}
              />
            ))}
            <Button
              variant="text"
              onClick={() => {
                onChange({ ...value, filters: {} });
              }}
            >
              {t('actions.clearFilters')}
            </Button>
          </Stack>
        )}
      </Stack>
    );
  }

  return (
    <Stack
      spacing={1.5}
      sx={(theme) =>
        // Prompt 22's tinted toolbar — suite keeps it; berry's sits plain inside the table card.
        theme.eis.surface.filterBarTint
          ? {
              py: 1.5,
              px: 1.5,
              mx: -1.5,
              bgcolor: 'surfaces.raised',
              borderRadius: `${String(theme.eis.surface.controlRadius)}px`,
            }
          : {}
      }
    >
      <Stack
        direction="row"
        spacing={1.5}
        useFlexGap
        sx={{ alignItems: 'center', flexWrap: 'wrap' }}
      >
        <TextField
          value={value.search}
          onChange={(event) => {
            onChange({ ...value, search: event.target.value });
          }}
          placeholder={searchPlaceholder ?? t('filter.searchPlaceholder')}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
            htmlInput: { 'aria-label': t('actions.search') },
          }}
          sx={{ minWidth: 260, flex: '0 1 320px' }}
        />
        {savedViews !== undefined && <Box>{savedViews}</Box>}
        <Box sx={{ flex: 1 }} />
        {resultCount !== undefined && (
          <Typography variant="body2" color="text.secondary" aria-live="polite">
            {t('filter.resultCount', { count: resultCount })}
          </Typography>
        )}
      </Stack>

      <Stack direction="row" spacing={2} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
        {definitions.map((definition) => (
          <Stack
            key={definition.field}
            direction="row"
            spacing={0.75}
            useFlexGap
            role="group"
            aria-label={definition.label}
            sx={{ alignItems: 'center', flexWrap: 'wrap' }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ mr: 0.25 }}>
              {definition.label}
            </Typography>
            {definition.choices.map((choice) => {
              const selected = (value.filters[definition.field] ?? []).includes(choice.value);
              return (
                <Chip
                  key={choice.value}
                  label={choiceLabel(choice)}
                  variant={selected ? 'filled' : 'outlined'}
                  color={selected ? 'primary' : 'default'}
                  onClick={() => {
                    toggle(definition, choice);
                  }}
                  aria-pressed={selected}
                  component="button"
                  type="button"
                />
              );
            })}
          </Stack>
        ))}
        {activeCount > 0 && (
          <Button
            variant="text"
            onClick={() => {
              onChange({ ...value, filters: {} });
            }}
          >
            {t('actions.clearFilters')}
          </Button>
        )}
      </Stack>
    </Stack>
  );
}
